import get from "lodash/get";
import { Path } from "react-hook-form";

import {
  CancellationPairEntry,
  compareReturnedBeforeCancelled,
} from "@/utils/ledgerCancellationOrder";

type GroupByFieldOptions<T> = {
  fallbackField?: Path<T>;
  transformGroupKey?: (key: string) => string;
  sortItems?: (a: T, b: T) => number;
  sortBeforeGrouping?: (a: T, b: T) => number;
};

/**
 * Groups items by a specified field and returns an array of [key, items] tuples
 * sorted by key in descending order
 */
const groupByField = <T>(
  items: T[],
  field: Path<T>,
  options: GroupByFieldOptions<T> = {}
): Array<[string, T[]]> => {
  const { fallbackField, transformGroupKey, sortItems, sortBeforeGrouping } =
    options;

  // Apply pre-grouping sort if specified
  const itemsToGroup = sortBeforeGrouping
    ? [...items].sort(sortBeforeGrouping)
    : items;

  // Group items by field value
  const groups = itemsToGroup.reduce(
    (acc, item) => {
      // Get the group key, with fallback and transformation
      let key = (get(item, field) ??
        get(item, fallbackField ?? "") ??
        "") as string;

      if (transformGroupKey) {
        key = transformGroupKey(key);
      }

      // Initialize group if it doesn't exist
      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key]!.push(item);
      return acc;
    },
    {} as Record<string, T[]>
  );

  // Convert to array of tuples and sort within groups if needed
  const groupedArray = Object.entries(groups).map(([key, groupItems]) => {
    const sortedItems = sortItems
      ? [...groupItems].sort(sortItems)
      : groupItems;
    return [key, sortedItems] as [string, T[]];
  });

  // Sort groups by key in descending order
  return groupedArray.sort((a, b) => b[0].localeCompare(a[0]));
};

export default groupByField;

/**
 * Helper sorting function for fx-tickets entries
 * Sorts entries with positive balance before negative balance
 */
export const compareFxTickets = <T>(a: T, b: T): number => {
  const aEntry = a as LedgerEntryResolvedType;
  const bEntry = b as LedgerEntryResolvedType;

  // Only sort fx-tickets entries with the same UUID
  if (
    aEntry.journal === "fx-tickets" &&
    bEntry.journal === "fx-tickets" &&
    aEntry.uuid === bEntry.uuid
  ) {
    const aBalance = aEntry.balance || 0;
    const bBalance = bEntry.balance || 0;

    // Positive balance comes before negative balance
    if (bBalance > 0 && aBalance <= 0) return 1;
    if (aBalance > 0 && bBalance <= 0) return -1;

    // If both have the same sign, sort by absolute value (larger first)
    return Math.abs(bBalance) - Math.abs(aBalance);
  }

  // Maintain original order for non-fx-tickets entries
  return 0;
};

/**
 * Sorts ledger entries by createTime in descending order (newest first)
 * Falls back to valueDate if updateTime is not available
 */
export const compareByCreateTime = <T>(a: T, b: T): number => {
  const aEntry = a as LedgerEntryResolvedType;
  const bEntry = b as LedgerEntryResolvedType;

  // Get create times from resolvedData.meta.createTime
  const aTime =
    aEntry.resolvedData?.meta?.createTime ||
    aEntry.resolvedData?.valueDate ||
    aEntry.valueDate;
  const bTime =
    bEntry.resolvedData?.meta?.createTime ||
    bEntry.resolvedData?.valueDate ||
    bEntry.valueDate;

  if (!aTime && !bTime) return 0;
  if (!aTime) return 1;
  if (!bTime) return -1;

  // Sort in descending order (newest first)
  return (
    new Date(bTime as string).getTime() - new Date(aTime as string).getTime()
  );
};

/**
 * Comparator for the on-screen activity list (newest first), in priority
 * order: fx-ticket leg order, then createTime descending, then
 * returned-before-cancelled.
 *
 * Not to be confused with `sortStatementEntries`, which orders the same entries
 * for CSV/PDF export and sorts oldest-first by bookDate. The two surfaces read
 * the legs of a cancelled transaction in the same chronological sense, so their
 * pair orders are mirror images: Returned above Cancelled here, Cancelled above
 * Returned in the statement. See utils/ledgerCancellationOrder.ts.
 */
export const compareLedgerEntries = <T>(a: T, b: T): number => {
  // First apply fx-tickets sorting
  const fxSort = compareFxTickets(a, b);
  if (fxSort !== 0) return fxSort;

  // Then sort by createTime
  const timeSort = compareByCreateTime(a, b);
  if (timeSort !== 0) return timeSort;

  // Last: both legs of a cancelled transaction resolve to the same upstream
  // record and so share a createTime. Without this tie-break the pair renders in
  // whatever order the backend happened to serialise it, flipping the Cancelled
  // and Returned rows. Newest first means the return (the later effect) sits
  // above the cancellation. Applied last to keep the comparator transitive.
  return compareReturnedBeforeCancelled(
    a as CancellationPairEntry,
    b as CancellationPairEntry
  );
};

/**
 * Combines FX ticket pairs (entries with same uuid) into a single CombinedFxEntry.
 * Used on dashboard to show both legs of an FX exchange as a single row.
 * Non-FX entries and unpaired FX entries are returned as-is.
 */
export const combineFxTicketPairs = (
  entries: LedgerEntryResolvedType[]
): LedgerEntryOrCombined[] => {
  const result: LedgerEntryOrCombined[] = [];
  const fxTicketsByUuid = new Map<string, LedgerEntryResolvedType[]>();

  // Separate FX tickets from other entries, group FX tickets by uuid
  for (const entry of entries) {
    if (entry.journal === "fx-tickets") {
      const existing = fxTicketsByUuid.get(entry.uuid) || [];
      existing.push(entry);
      fxTicketsByUuid.set(entry.uuid, existing);
    } else {
      result.push(entry);
    }
  }

  // Process FX tickets - combine pairs, keep singles as-is
  for (const [uuid, fxEntries] of fxTicketsByUuid) {
    if (fxEntries.length === 2) {
      // Find credit (positive) and debit (negative) entries
      // Handle edge case where balance might be 0 or undefined
      const creditEntry = fxEntries.find((e) => (e.balance ?? 0) > 0);
      const debitEntry = fxEntries.find((e) => (e.balance ?? 0) < 0);

      if (creditEntry && debitEntry) {
        // FX ticket resolvedData may be either:
        // - Flattened (BFF): { meta: { createTime, updateTime }, baseCurrency, ... }
        // - Wrapped (legacy): { data: [{ meta: { createTime, updateTime }, ... }] }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const creditRd = creditEntry.resolvedData as any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const debitRd = debitEntry.resolvedData as any;
        const creditTrade = creditRd?.data?.[0] ?? creditRd;
        const debitTrade = debitRd?.data?.[0] ?? debitRd;

        // Create combined entry
        const combined: CombinedFxEntry = {
          isCombined: true,
          creditEntry: creditEntry as LedgerEntryResolvedFxTicket,
          debitEntry: debitEntry as LedgerEntryResolvedFxTicket,
          uuid,
          journal: "fx-tickets",
          valueDate: creditEntry.valueDate || debitEntry.valueDate || "",
          resolvedData: {
            meta: {
              createTime:
                creditTrade?.meta?.createTime ||
                debitTrade?.meta?.createTime ||
                "",
              updateTime:
                creditTrade?.meta?.updateTime ||
                debitTrade?.meta?.updateTime ||
                "",
            },
          },
        };
        result.push(combined);
      } else {
        // Both entries have same sign, zero balance, or missing - keep separate
        result.push(...fxEntries);
      }
    } else {
      // Single entry (pagination edge case), more than 2, or empty - keep as-is
      result.push(...fxEntries);
    }
  }

  return result;
};
